import logger from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../common/LoadingSpinner';
import PasswordStrengthMeter from '../common/PasswordStrengthMeter';
import { ArrowRight, Eye, EyeOff, CheckCircle2, Mail } from 'lucide-react';
import axios from 'axios';
import AuthCard from '../brand/AuthCard';

const inputClass =
  'w-full px-3 py-2.5 bg-[rgba(20,18,28,0.7)] border border-white/10 rounded-lg text-sm text-white placeholder-white/25 outline-none focus:border-violet-400/55 focus:bg-[rgba(24,21,34,0.9)] focus:ring-2 focus:ring-violet-500/20 transition-colors';

// Studio is invite-only for now: existing users sign in as usual, the sign-up
// screen explains how to ask for an invitation. Build with
// REACT_APP_INVITE_ONLY=false to bring the sign-up form back.
const INVITE_ONLY = process.env.REACT_APP_INVITE_ONLY !== 'false';
const INVITE_EMAIL = 'contact@thinkingdbx.com';

const SignupForm = ({ onToggleForm }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [authConfig, setAuthConfig] = useState({
    localAuthEnabled: true,
    selfRegistrationEnabled: true,
    ldapEnabled: false,
    oidcEnabled: false,
    googleOAuthEnabled: true,
    oidcProviderName: 'SSO',
  });

  const { signup } = useAuth();

  useEffect(() => {
    const fetchAuthConfig = async () => {
      try {
        const response = await axios.get('/api/auth/config');
        if (response.data?.success && response.data?.data) {
          setAuthConfig(prev => ({ ...prev, ...response.data.data }));
        }
      } catch (err) {
        logger.warn('Failed to fetch auth config, using defaults:', err);
      }
    };
    fetchAuthConfig();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setIsLoading(true);

    if (!email || !password || !fullName) {
      setError('Please fill in all fields');
      setIsLoading(false);
      return;
    }

    if (!acceptedTerms) {
      setError('You must accept the Terms of Service and Privacy Policy to create an account');
      setIsLoading(false);
      return;
    }

    try {
      const result = await signup(email, password, fullName);
      if (result.success) {
        setSuccess(true);
        setEmail('');
        setPassword('');
        setFullName('');
      } else {
        setError(result.error || 'Signup failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      logger.error('Signup error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setIsLoading(true);
    try {
      const response = await axios.post('/api/auth/google/callback', {
        credential: credentialResponse.credential
      });
      if (response.data.success) {
        const { token, user } = response.data.data;
        if (token) localStorage.setItem('token', token);
        if (user) localStorage.setItem('user', JSON.stringify(user));
        window.location.href = '/home';
      } else {
        setError(response.data.message || 'Google Sign-In failed');
      }
    } catch (err) {
      logger.error('Google Sign-In error:', err);
      setError(err.response?.data?.message || 'Google Sign-In failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google Sign-In was cancelled or failed. Please try again.');
  };

  const handleLdapLogin = async () => {
    if (!email || !password) {
      setError('Please enter your username and password for LDAP login');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const res = await axios.post('/api/auth/ldap/login', { email, password });
      if (res.data?.success && res.data?.data) {
        window.location.href = '/home';
      } else {
        setError(res.data?.message || 'LDAP login failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'LDAP authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (INVITE_ONLY) {
    return (
      <AuthCard title="By invitation only" subtitle="Studio is currently available to invited users.">
        <div className="text-center py-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-violet-500/15 border border-violet-500/30 mb-4">
            <Mail size={26} className="text-violet-300" />
          </div>
          <p className="text-sm text-white/60 mb-6 leading-relaxed">
            To request an invitation, email{' '}
            <a
              href={`mailto:${INVITE_EMAIL}?subject=Studio%20invitation%20request`}
              className="text-violet-300 hover:text-violet-200 font-medium underline underline-offset-2"
            >
              {INVITE_EMAIL}
            </a>
            .
          </p>
          <button
            onClick={onToggleForm}
            className="w-full py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white text-sm font-semibold rounded-md transition-all inline-flex items-center justify-center gap-2 shadow-lg shadow-violet-900/20"
          >
            Already have an account? Sign in <ArrowRight size={16} />
          </button>
        </div>
      </AuthCard>
    );
  }

  if (success) {
    return (
      <AuthCard title="Account created" subtitle="Welcome to Studio.">
        <div className="text-center py-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 mb-4">
            <CheckCircle2 size={28} className="text-emerald-400" />
          </div>
          <p className="text-sm text-white/60 mb-6 leading-relaxed">
            Please check your email to verify your account before signing in.
          </p>
          <button
            onClick={onToggleForm}
            className="w-full py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white text-sm font-semibold rounded-md transition-all inline-flex items-center justify-center gap-2 shadow-lg shadow-violet-900/20"
          >
            Go to Sign In <ArrowRight size={16} />
          </button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Start free trial" subtitle="Create your Studio account — no credit card required.">
      {error && (
        <div className="mb-4 px-3 py-2.5 rounded-md border border-red-500/30 bg-red-500/10">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="fullName" className="block text-xs font-medium text-white/60 mb-1.5">Full Name</label>
          <input
            id="fullName"
            type="text"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="John Doe"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-xs font-medium text-white/60 mb-1.5">Email</label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-xs font-medium text-white/60 mb-1.5">Password</label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a strong password"
              className={`${inputClass} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-white/40 hover:text-white/70 transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="mt-2">
            <PasswordStrengthMeter
              password={password}
              email={email}
              fullName={fullName}
              showRequirements={true}
            />
          </div>
        </div>

        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="terms"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mt-0.5 w-3.5 h-3.5 rounded bg-zinc-900 border-white/20 cursor-pointer flex-shrink-0 accent-violet-500"
          />
          <label htmlFor="terms" className="text-xs text-white/55 leading-relaxed">
            I agree to the{' '}
            <a href="/legal?doc=terms" target="_blank" rel="noopener noreferrer" className="underline text-white/75 hover:text-violet-300 transition-colors">Terms of Service</a>,{' '}
            <a href="/legal?doc=privacy" target="_blank" rel="noopener noreferrer" className="underline text-white/75 hover:text-violet-300 transition-colors">Privacy Policy</a>,{' '}
            <a href="/legal?doc=refund" target="_blank" rel="noopener noreferrer" className="underline text-white/75 hover:text-violet-300 transition-colors">Refund Policy</a>, and{' '}
            <a href="/legal?doc=dpa" target="_blank" rel="noopener noreferrer" className="underline text-white/75 hover:text-violet-300 transition-colors">Data Processing Agreement</a>
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white text-sm font-semibold rounded-md transition-all inline-flex items-center justify-center gap-2 shadow-lg shadow-violet-900/20 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <><LoadingSpinner size="sm" /> Creating account...</>
          ) : (
            <>Create account <ArrowRight size={16} /></>
          )}
        </button>
      </form>

      {(authConfig.googleOAuthEnabled || authConfig.ldapEnabled) && (
        <div className="my-5 flex items-center gap-3">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-white/30">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
      )}

      {authConfig.googleOAuthEnabled && (
        <div className="flex justify-center mb-3">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            useOneTap={false}
            theme="filled_black"
            size="large"
            text="continue_with"
            shape="rectangular"
            logo_alignment="center"
          />
        </div>
      )}

      {authConfig.ldapEnabled && (
        <button
          type="button"
          onClick={handleLdapLogin}
          className="w-full py-2.5 border border-white/15 text-sm font-medium rounded-md hover:border-violet-500/40 hover:bg-white/5 transition-colors bs-text"
        >
          Sign in with LDAP / Active Directory
        </button>
      )}

      <p className="mt-6 text-center text-sm text-white/55">
        Already have an account?{' '}
        <button onClick={onToggleForm} className="font-semibold text-violet-300 hover:text-violet-200 transition-colors">
          Sign in
        </button>
      </p>
    </AuthCard>
  );
};

export default SignupForm;
